/* eslint-disable */
/**
 * @flow
 *
 * Custom controller extending ScrollPanCameraController in order to add vertical bumpers
 * default values for the verticalBumper is undefined and vertical bumpers are not applied by default
 * Can be adjusted via calls of
 * new CustomScrollPanCameraController({frame, fov, verticalBumper})
 * in client.js
 *
 */
import {type Quaternion, type Vec3} from 'virtual-tour-viewer/src/modules/React360Proxy';
import {ScrollPanCameraController} from 'virtual-tour-viewer/src/modules/React360Proxy';
import CameraUtils from '../utils/CameraUtils';
import Event from '../utils/Event';

const HALF_PI = Math.PI / 2;

export default class CustomScrollPanCameraController extends ScrollPanCameraController {
    verticalBumper: number;
    _handledEventEvent: Event;
    _scrollEvent: Event;

    constructor({frame, fov, verticalBumper}) {
        super(frame, fov);
        this.verticalBumper = verticalBumper;
        this._handledEventEvent = new Event();
        this._scrollEvent = new Event();
    }

    subscribeHandledEvent(callback) {
        this._handledEventEvent.subscribe(callback);
    }

    unsubscribeHandledEvent(callback) {
        this._handledEventEvent.unsubscribe(callback);
    }

    _publishHandledEvent(eventArgs) {
        this._handledEventEvent.publish(this, eventArgs);
    }

    doOnceScroll(callback) {
        this._scrollEvent.doOnce(callback);
    }

    _publishScroll(eventArgs) {
        this._scrollEvent.publish(this, eventArgs);
    }

    _onWheel(e: WheelEvent) {
        /**
         * We are overriding this method because the original
         * implementation from react 360 switches pitch and yaw calculations.
         */
        if (!this._enabled) {
            return;
        }
        this._publishHandledEvent(e);
        const width = this._frame.clientWidth;
        const height = this._frame.clientHeight;
        const aspect = width / height;
        const deltaX = e.deltaX;
        const deltaY = e.deltaY;
        this._deltaYaw += (deltaX / width) * this._verticalFov * aspect;
        this._deltaPitch += (deltaY / height) * this._verticalFov;
        this._deltaPitch = Math.max(-HALF_PI, Math.min(HALF_PI, this._deltaPitch));
        e.preventDefault();
        this._publishScroll(e);
    }

    fillCameraProperties(position: Vec3, rotation: Quaternion): boolean {
        if (!this._enabled) {
            return false;
        }

        if (this._deltaPitch === 0 && this._deltaYaw === 0) {
            return false;
        }

        // premultiply the camera rotation by the horizontal (pitch) rotation,
        // then multiply by the vertical (yaw) rotation

        let newRotation = CameraUtils.rotateCamera(rotation, this._deltaYaw, this._deltaPitch);

        if (this.verticalBumper) {
            if (newRotation[3] === 0) {
                return false;
            }
            const verticalTangent = newRotation[0] / newRotation[3];
            const maximumTangentAbs = Math.tan(this.verticalBumper / 2);
            if (
                (this.verticalBumper && verticalTangent >= maximumTangentAbs) ||
                verticalTangent <= -maximumTangentAbs
            ) {
                newRotation = CameraUtils.rotateCamera(rotation, this._deltaYaw, 0);
            }
        }

        rotation[0] = newRotation[0];
        rotation[1] = newRotation[1];
        rotation[2] = newRotation[2];
        rotation[3] = newRotation[3];

        this._deltaPitch = 0;
        this._deltaYaw = 0;
        return true;
    }
}
